import { shippingState, protectorShippingOffer } from '@theme/armony-shipping-core';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { findProtectorVariant, initialChoice, normalizeModel, confirmAddIntent } from '@theme/armony-protector-core';

const cartRoot = window.Shopify?.routes?.root || '/';
const money = (price) => '$' + Math.round(price / 100).toLocaleString('es-CO');
let addSequence = 0;
let confirmedIntent = { sequence: 0, variantId: '' };
let addingProtector = false;
const variantId = (value) => String(value || '').split('/').pop();

class ArmonyProtectorUpsell extends HTMLElement {
  static observedAttributes = ['data-cart-signature'];
  #controller;
  #caseVariant = '';
  #model = '';
  #type = 'Clear';
  #config;

  connectedCallback() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.addEventListener('change', this.#change, { signal: this.#controller.signal });
    this.addEventListener('submit', this.#submit, { signal: this.#controller.signal });
    this.refresh();
  }

  disconnectedCallback() { this.#controller?.abort(); }
  attributeChangedCallback() { queueMicrotask(() => { if (this.isConnected) this.refresh(); }); }

  refresh() {
    const payload = this.querySelector('[data-protector-config]');
    if (!payload) return;
    try { this.#config = JSON.parse(payload.textContent); } catch { return; }
    const cases = this.#config.cases;
    const source = cases.find((item) => variantId(item.variantId) === confirmedIntent.variantId)
      || cases.find((item) => variantId(item.variantId) === this.#caseVariant) || cases[0];
    if (!source) return;
    if (this.#caseVariant !== variantId(source.variantId)) {
      const choice = initialChoice(this.#config.variants, source.model, this.#type);
      this.#caseVariant = variantId(source.variantId);
      this.#model = choice.model;
      this.#type = choice.type;
    }
    this.#paint(source.model);
  }

  #change = (event) => {
    if (addingProtector) return;
    if (event.target.matches('[data-protector-type]')) this.#type = event.target.value;
    if (event.target.matches('[data-protector-model]')) this.#model = event.target.value;
    const source = this.#config.cases.find((item) => variantId(item.variantId) === this.#caseVariant);
    this.#paint(source?.model || '');
  };

  #paint(caseModel) {
    const selected = findProtectorVariant(this.#config.variants, this.#type, this.#model);
    const benefit = this.querySelector('[data-protector-benefit]');
    if (benefit) {
      const labels = this.#config.labels;
      const state = shippingState(this.#config.amount, this.#config.currency, this.#config.country);
      benefit.hidden = !selected?.available;
      if (selected?.available) {
        const offer = protectorShippingOffer(this.#config.amount, selected.price, this.#config.currency, this.#config.country);
        const format = text => text.replace('{total}', money(offer.total)).replace('{extra}', money(offer.extra)).replace('{saved}', money(offer.saved));
        const heading = document.createElement('strong');
        const detail = document.createElement('span');
        heading.textContent = offer.unlocks ? labels.unlock : state.unlocked ? labels.already : labels.generic;
        detail.textContent = offer.unlocks ? format(labels.comparison) : offer.eligible && !state.unlocked ? format(labels.below) : '';
        benefit.replaceChildren(heading, detail);
      }
    }
    this.querySelectorAll('[data-protector-type]').forEach((input) => {
      input.checked = input.value === this.#type;
      input.disabled = addingProtector;
    });
    const model = this.querySelector('[data-protector-model]');
    if (model) {
      model.value = this.#model;
      model.disabled = addingProtector;
      [...model.options].forEach((option) => {
        if (!option.value) return;
        const match = findProtectorVariant(this.#config.variants, this.#type, option.value);
        option.disabled = !match?.available;
        option.textContent = `${option.value}${match?.available ? '' : ' · Agotado'}`;
      });
    }
    const price = this.querySelector('[data-protector-price]');
    if (price) price.textContent = selected ? money(selected.price) : 'Desde ' + money(Math.min(...this.#config.variants.map((item) => item.price)));
    const input = this.querySelector('[data-protector-variant]');
    if (input) input.value = selected?.available ? selected.id : '';
    const button = this.querySelector('[data-protector-add]');
    if (button) {
      button.disabled = addingProtector || !selected?.available;
      button.textContent = addingProtector ? 'Agregando…'
        : !this.#model ? 'Elige tu iPhone'
          : !selected?.available ? 'Agotado para esta combinación' : `Agregar protector · ${money(selected.price)}`;
      button.setAttribute('aria-busy', String(addingProtector));
    }
    const exactModelExists = this.#config.variants.some((item) => normalizeModel(item.options[1]) === normalizeModel(caseModel));
    const note = this.querySelector('[data-protector-note]');
    if (note) {
      note.hidden = !caseModel || exactModelExists || !!this.#model;
      note.textContent = `Aún no tenemos protector para ${/^iphone/i.test(caseModel) ? caseModel : 'iPhone ' + caseModel}.`;
    }
  }

  #submit = async (event) => {
    if (!event.target.matches('[data-protector-form]')) return;
    event.preventDefault();
    const selected = findProtectorVariant(this.#config.variants, this.#type, this.#model);
    if (addingProtector || !selected?.available) return;
    addingProtector = true;
    this.#paint('');
    const error = this.querySelector('[data-protector-error]');
    if (error) error.hidden = true;
    const checkoutButtons = [...document.querySelectorAll('button[name="checkout"]')];
    const checkoutStates = checkoutButtons.map((button) => button.disabled);
    checkoutButtons.forEach((button) => { button.disabled = true; });
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId).filter(Boolean))];
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add', context: 'cart', lines: [{ merchandiseId: String(selected.id), quantity: 1 }],
      promise: deferred.promise,
    }));
    let added = false;
    try {
      const response = await fetch(`${cartRoot}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: selected.id, quantity: 1 }], sections, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'No pudimos agregar el protector. Intenta de nuevo.');
      added = true;
      // A confirmed add suppresses retries immediately, even if section rendering is delayed.
      document.querySelectorAll('armony-protector-upsell').forEach((element) => { element.hidden = true; });
      const cartResponse = await fetch(`${cartRoot}cart.js`, { headers: { Accept: 'application/json' } });
      if (!cartResponse.ok) throw new Error('No pudimos actualizar el carrito.');
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: {
        sections: result.sections, items: cart.items, itemCount: cart.item_count, source: 'armony-protector', didError: false,
      } });
    } catch (failure) {
      deferred.reject(failure);
      if (added) {
        window.location.assign(`${cartRoot}cart`);
        return;
      }
      if (error) { error.textContent = failure.message; error.hidden = false; }
      try {
        const response = await fetch(`${cartRoot}products/${this.#config.handle}.js`);
        if (response.ok) this.#config.variants = (await response.json()).variants;
      } catch { /* Keep Shopify's error visible and allow a later retry. */ }
    } finally {
      addingProtector = false;
      checkoutButtons.forEach((button, index) => { button.disabled = checkoutStates[index]; });
      this.#paint('');
    }
  };
}

if (!customElements.get('armony-protector-upsell')) customElements.define('armony-protector-upsell', ArmonyProtectorUpsell);

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const intent = event.action === 'add'
    ? { sequence: ++addSequence, variantId: variantId(event.lines?.[0]?.merchandiseId) } : null;
  event.promise?.then((result) => {
    if (intent) confirmedIntent = confirmAddIntent(confirmedIntent, intent, result);
    requestAnimationFrame(() => {
      document.querySelectorAll('armony-protector-upsell').forEach((element) => element.refresh());
    });
  }).catch(() => {});
});

// A checkout submit must not outrun a protector addition already in flight.
document.addEventListener('submit', (event) => {
  if (addingProtector && event.submitter?.name === 'checkout') event.preventDefault();
}, true);

